<template>
  <LineChart
    chart-id="revisions-line-chart"
    :chart-data="chartData"
    :options="options"
    :styles="{
      width: '100%',
      height: '100%',
    }"
  />
</template>

<script>
import LineChart from '@/components/charts/LineChart.vue';
import { numberWithCommas } from '@/helpers/formatters';
import { mapState } from 'pinia';
import { useUsage } from '@/stores/insights/usage';
import { useColors } from '@/composables/colors';

export default {
  name: 'RevisionsLineChart',
  components: {
    LineChart,
  },
  setup() {
    const {
      textDarkest, brandPrimary, basicBlue, basicGreen,
    } = useColors();

    return {
      textDarkest, brandPrimary, basicBlue, basicGreen,
    };
  },
  computed: {
    ...mapState(useUsage, ['postRevisionsDate']),
    formattedDateData() {
      const labels = [];
      const totalRevisions = { label: 'Total Revisions', data: [] };
      const totalUsers = { label: 'Total Users', data: [] };
      const totalPosts = { label: 'Total Posts', data: [] };

      this.postRevisionsDate.forEach((dateInstance) => {
        labels.push(dateInstance.label);
        totalRevisions.data.push(dateInstance.totalRevisions);
        totalUsers.data.push(dateInstance.totalUsers);
        totalPosts.data.push(dateInstance.totalPosts);
      });

      return {
        labels,
        datasets: [totalRevisions, totalUsers, totalPosts],
      };
    },
    chartData() {
      const styledDatasets = this.formattedDateData?.datasets.map((dataset, i) => {
        const lineColors = [this.brandPrimary, this.basicBlue, this.basicGreen];

        return {
          ...dataset,
          borderColor: lineColors[i],
          borderWidth: 2,
          pointBackgroundColor: lineColors[i],
          pointRadius: 2,
          backgroundColor: i === 0 ? 'rgba(71, 69, 213, 0.1)' : undefined,
          fill: i === 0,
          yAxisID: i === 0 ? 'y' : 'y1',
        };
      });
      return { ...this.formattedDateData, datasets: styledDatasets };
    },
    options() {
      return {
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          tooltip: {
            mode: 'index',
            backgroundColor: 'white',
            bodySpacing: 5,
            bodyFont: {
              size: 14,
              family: 'Inter',
            },
            bodyColor: this.textDarkest,
            titleColor: this.textDarkest,
            titleFont: {
              size: 16,
              family: 'Inter',
            },
            padding: {
              y: 12,
              x: 6,
            },
            callbacks: {
              title: (tooltipItems) => tooltipItems[0].label,
              label: (tooltipItem) => {
                const label = tooltipItem.dataset.label || '';
                const dataPoint = tooltipItem.dataset.data[tooltipItem.dataIndex] || '';
                return ` ${label.replace('Total ', '')}: ${numberWithCommas(dataPoint) || 0}`;
              },
            },
          },
          legend: {
            position: 'bottom',
            labels: {
              usePointStyle: true,
              boxWidth: 4,
              boxHeight: 4,
            },
          },
        },
        scales: {
          y: {
            type: 'linear',
            position: 'left',
            title: {
              display: true,
              text: 'Revisions',
            },
            ticks: {
              maxTicksLimit: 11,
              padding: 10,
              beginAtZero: true,
            },
            border: {
              display: false,
            },
            grid: {
              drawTicks: false,
              zeroLineWidth: 0,
            },
          },
          y1: {
            type: 'linear',
            position: 'right',
            title: {
              display: true,
              text: 'Users/Posts',
            },
            ticks: {
              maxTicksLimit: 11,
              padding: 10,
              beginAtZero: true,
            },
            grid: {
              display: false,
              zeroLineWidth: 0,
            },
          },
          x: {
            ticks: {
              padding: 10,
            },
            grid: {
              display: false,
            },
          },
        },
      };
    },
  },
};
</script>
